import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, AlertTriangle } from 'lucide-react';
import { SearchableCombobox, createItemOptions } from '../UI/SearchableCombobox';
import { Item, Unit, WarehouseInventory } from '../../types/entities';
import toast from 'react-hot-toast';

export interface WarehouseItemRow {
  id: string; // Temporary ID for form tracking
  itemId: number | null;
  itemName: string;
  quantity: number;
  unit: Unit;
}

interface Props {
  items: WarehouseItemRow[];
  onChange: (items: WarehouseItemRow[]) => void;
  catalogItems: Item[];
  showCurrentStock?: boolean;
  inventory?: WarehouseInventory[]; // Current stock per item in selected warehouse
}

const UNIT_OPTIONS: Unit[] = ['M3', 'TON', 'KG', 'PIECE'];

export const WarehouseItemsTable: React.FC<Props> = ({
  items,
  onChange,
  catalogItems,
  showCurrentStock = false,
  inventory = [],
}) => {
  const { t } = useTranslation();

  const itemOptions = useMemo(() => createItemOptions(catalogItems), [catalogItems]);

  // Build a map: itemId → currentStock for quick lookup
  const stockMap = useMemo(() => {
    const map: Record<number, number> = {};
    inventory.forEach((inv) => {
      map[inv.itemId] = inv.currentStock;
    });
    return map;
  }, [inventory]);

  const handleAddRow = () => {
    const newRow: WarehouseItemRow = {
      id: `temp-${Date.now()}`,
      itemId: null,
      itemName: '',
      quantity: 0,
      unit: 'M3',
    };
    onChange([...items, newRow]);
  };

  const handleRemoveRow = (id: string) => {
    if (items.length === 1) {
      toast.error(t('warehouse.itemsTable.minOneItem'));
      return;
    }
    onChange(items.filter((item) => item.id !== id));
  };

  const handleItemSelect = (rowId: string, selectedId: string | number | null) => {
    const found = catalogItems.find((i) => i.id === selectedId);
    const updated = items.map((row) => {
      if (row.id !== rowId) return row;
      if (!found) {
        return { ...row, itemId: null, itemName: '', unit: 'M3' as Unit };
      }
      return {
        ...row,
        itemId: found.id,
        itemName: found.name,
        unit: found.defaultUnit,
      };
    });
    onChange(updated);
  };

  const handleUpdateField = (id: string, field: 'quantity' | 'unit', value: any) => {
    const updated = items.map((row) =>
      row.id === id ? { ...row, [field]: value } : row
    );
    onChange(updated);
  };

  const getStockDisplay = (row: WarehouseItemRow): { value: string; isLow: boolean } => {
    if (!row.itemId) return { value: '-', isLow: false };
    const stock = stockMap[row.itemId];
    if (stock === undefined) return { value: '-', isLow: false };
    const isLow = stock <= 0 || row.quantity > stock;
    return { value: stock.toLocaleString('vi-VN'), isLow };
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8">
                #
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('warehouse.itemsTable.item')} <span className="text-red-500">*</span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                {t('warehouse.itemsTable.quantity')} <span className="text-red-500">*</span>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                {t('warehouse.itemsTable.unit')}
              </th>
              {showCurrentStock && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  {t('warehouse.itemsTable.currentStock')}
                </th>
              )}
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                {t('common.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {items.map((row, index) => {
              const stock = showCurrentStock ? getStockDisplay(row) : null;
              const isInsufficient = stock?.isLow && row.quantity > 0;

              return (
                <tr
                  key={row.id}
                  className={isInsufficient ? 'bg-red-50' : undefined}
                >
                  {/* Index */}
                  <td className="px-4 py-3 text-sm text-gray-400 text-center">
                    {index + 1}
                  </td>

                  {/* Item SearchableCombobox */}
                  <td className="px-4 py-3">
                    <SearchableCombobox
                      options={itemOptions}
                      value={row.itemId}
                      onChange={(val) => handleItemSelect(row.id, val)}
                      placeholder={t('warehouse.itemsTable.selectItem')}
                    />
                  </td>

                  {/* Quantity */}
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      value={row.quantity || ''}
                      onChange={(e) =>
                        handleUpdateField(row.id, 'quantity', parseFloat(e.target.value) || 0)
                      }
                      className={`input-field w-full ${
                        row.quantity <= 0 ? 'border-red-300 focus:border-red-400' : ''
                      }`}
                      placeholder="0"
                    />
                  </td>

                  {/* Unit */}
                  <td className="px-4 py-3">
                    <select
                      value={row.unit}
                      onChange={(e) =>
                        handleUpdateField(row.id, 'unit', e.target.value as Unit)
                      }
                      className="input-field w-full"
                    >
                      {UNIT_OPTIONS.map((unit) => (
                        <option key={unit} value={unit}>
                          {t(`items.units.${unit}`)}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Current Stock */}
                  {showCurrentStock && stock && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {isInsufficient && (
                          <AlertTriangle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                        )}
                        <span
                          className={`text-sm font-medium ${
                            stock.isLow ? 'text-red-600' : 'text-gray-700'
                          }`}
                        >
                          {stock.value}
                        </span>
                      </div>
                    </td>
                  )}

                  {/* Remove */}
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(row.id)}
                      disabled={items.length === 1}
                      className="text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title={t('warehouse.itemsTable.removeItem')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add Row Button */}
      <button
        type="button"
        onClick={handleAddRow}
        className="flex items-center gap-1.5 text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors"
      >
        <Plus className="w-4 h-4" />
        {t('warehouse.itemsTable.addItem')}
      </button>
    </div>
  );
};
